/**
 * AI Acquisition Coach product page — BP-025.
 * Evidence-backed next-best-action for every property.
 */
import { Brain, Gauge, ScrollText, ShieldCheck, GitBranch, Sparkles } from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

const heroMockup = (
  <ProductScreen title="ai coach / recommendation · 42 Coach Test Ln">
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <div className="text-[10px] uppercase text-emerald-800 font-bold tracking-wider">
        Recommendation
      </div>
      <div className="text-2xl font-bold text-emerald-900 mt-1">Pursue Subject-To</div>
      <div className="text-[11px] text-emerald-700 mt-1">
        Confidence 82% · 8 evidence sources · capped by data completeness
      </div>
    </div>
    <div className="mt-3 space-y-1 text-xs">
      {[
        ["Deal Score", "87 · Elite", "BP-005 v2.1"],
        ["Market Tier", "Hot · Score 83", "BP-024 v1.4"],
        ["Strategy Winner", "Subject-To (+$41k EV)", "BP-014 v3.0"],
        ["Buyer Fit", "3 verified matches", "BP-022 v2.0"],
      ].map(([k, v, bp]) => (
        <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-1">
          <span className="text-slate-600">{k}</span>
          <span className="font-semibold">{v}</span>
          <span className="text-[9px] font-mono text-emerald-600">{bp}</span>
        </div>
      ))}
    </div>
  </ProductScreen>
);

export default function AiAcquisitionCoachPage() {
  return (
    <ProductPageTemplate
      slug="ai-acquisition-coach"
      bp="BP-025 · AI Acquisition Coach"
      eyebrow="Product · AI Acquisition Coach"
      title="AI that explains the evidence."
      titleAccent="Not a black box."
      subtitle="The Coach synthesises every deterministic engine — Score, Strategy, Market, Buyer, Portfolio — into a single next-best-action per property, with an evidence panel citing the exact BP, version, and confidence for every claim."
      bullets={[
        "Every recommendation is grounded — no fabricated numbers, ever",
        "Confidence caps at 60% when critical data is missing",
        "One-click drill from insight → source engine → raw evidence",
      ]}
      heroMockup={heroMockup}
      capabilities={[
        { icon: Brain, title: "Grounded LLM Layer", desc: "Claude Sonnet 4.6 through emergentintegrations — synthesises engine outputs into plain-English rationale." },
        { icon: Gauge, title: "Confidence Envelope", desc: "Confidence is a mathematical function of data completeness. Missing comps → capped. No hallucinated conviction." },
        { icon: ScrollText, title: "Evidence Panel", desc: "Every claim links to the source BP + version + numeric value. Auditors love it. Investors trust it." },
        { icon: ShieldCheck, title: "Never Invents", desc: "If Repair estimator has 0 comps in ±20% mile radius, the Coach says so — instead of guessing." },
        { icon: GitBranch, title: "Why-Not Panel", desc: "For every rejected strategy, the Coach explains the numeric reason it lost — ending team debates." },
        { icon: Sparkles, title: "Portfolio Briefings", desc: "Weekly digest of the top 5 opportunities across your entire pipeline, ranked by expected value." },
      ]}
      howItWorks={[
        { step: 1, title: "Gather", desc: "Coach reads every deterministic engine's most recent output for the property." },
        { step: 2, title: "Weigh", desc: "Signals are combined via a versioned rule set. Confidence is computed from completeness." },
        { step: 3, title: "Synthesise", desc: "Claude Sonnet 4.6 drafts a plain-English recommendation citing each source engine." },
        { step: 4, title: "Cite", desc: "Every sentence links back to a BP, version, and evidence file — no black box." },
      ]}
      integrations={[
        { bp: "BP-005", name: "Deal Score" },
        { bp: "BP-014", name: "Strategy Comparison" },
        { bp: "BP-022", name: "Buyer Intelligence" },
        { bp: "BP-023", name: "Portfolio Intelligence" },
        { bp: "BP-024", name: "Market Intelligence" },
        { bp: "BP-004", name: "ARV Comps" },
        { bp: "BP-003", name: "Repair Estimator" },
        { bp: "BP-018", name: "Buyer Matching" },
      ]}
      proof={{
        stat: "82%",
        label: "avg. Coach confidence across shipped deals",
        quote: "The Why-Not panel ended our strategy debates in one meeting. Every rejected exit had a number attached to it.",
        attribution: "Acquisition team lead · Team plan · 18 seats",
      }}
      related={[
        { title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "The engine behind the Coach's exit-strategy winner." },
        { title: "Market Intelligence", to: "/products/market-intelligence", desc: "ZIP-level health and forecasts the Coach cites." },
        { title: "Property Workspace", to: "/products/property-workspace", desc: "Where the Coach lives, per property." },
      ]}
    />
  );
}
